import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import Translate from 'ui/atoms/translate';
import Number from 'ui/atoms/number';
import BigNumber from 'bignumber.js';
import { WalletType } from 'ui/types/wallet';
import { Money } from 'api';
import Currency from 'ui/atoms/currency';
import { toMoney, toNumber } from 'ui/helper/money';
import Accordion from 'ui/atoms/accordion';
import { NetworkTypeEnum } from 'api';
import CopyInputGroup from 'ui/molecules/copy-input-group';
import { makeModalForm } from 'src/ui/molecules/modal';
import Checkbox from 'ui/atoms/checkbox';
import * as Styled from './styled';
import makeLink from 'src/helper/make-link';
import { INVESTMENT_ROUTES } from 'src/subapps/investment/pages/routes.config';

export interface DepotTransferMaskFields {
  to: string;
  amount: string;
  isPlatformWallet: string;
  invitationLink?: string;
}

export interface DepotTransferInputMaskProps {
  /** Additional classes. */
  className?: string;

  onSubmit?: (values: DepotTransferMaskFields) => void;

  maxAmount?: BigNumber;

  walletType: WalletType;

  networkType: NetworkTypeEnum;

  nominalValue?: Money;

  amount?: string;

  isPlatformWallet: string;

  onChangeConfirmWallet: (isConfirmWalletSelected: boolean) => void;

  whiteListedError?: string;
}

export const DepotTransferInputMaskForm = makeModalForm<DepotTransferMaskFields>();

const DepotTransferInputMask: FunctionComponent<DepotTransferInputMaskProps> = (props) => {
  const { maxAmount, networkType, nominalValue, amount, isPlatformWallet, onChangeConfirmWallet, whiteListedError } =
    props;

  const [invitationLinkExpanded, setInvitationLinkExpanded] = useState(false);
  const [isConfirmWalletSelected, setConfirmWalletSelected] = useState(false);

  /*const balanceError: { balance: string } | null = error.getErrorCode(
      "transfer_insufficient_balance"
    );
    if (balanceError) {
      maxAmount = makeBigInt(balanceError.balance);
      handled = true;
    }
  }*/

  useEffect(() => {
    setConfirmWalletSelected(false);
  }, [isPlatformWallet]);

  const onChangeConfirmWalletCheckbox = useCallback(() => {
    onChangeConfirmWallet(!isConfirmWalletSelected);
    setConfirmWalletSelected(!isConfirmWalletSelected);
  }, [isConfirmWalletSelected]);

  return (
    <DepotTransferInputMaskForm.I18nKey i18nKey="depotTransferInputMask">
      {/* error.hasErrorCode("transfer_invalid_wallet") */}
      <DepotTransferInputMaskForm.Group
        fieldError={whiteListedError || <Translate name="portfolio.invalidWallet" />}
        name="to"
        required={true}
      >
        {networkType === NetworkTypeEnum.ETHEREUM && DepotTransferInputMaskForm.Validators.EthereumAddress()}
        {networkType === NetworkTypeEnum.ETHEREUM && DepotTransferInputMaskForm.Validators.TokenAddressChecksum()}
        {networkType === NetworkTypeEnum.STELLAR && DepotTransferInputMaskForm.Validators.StellarAddress()}
        <DepotTransferInputMaskForm.Input />
      </DepotTransferInputMaskForm.Group>
      {/* error.hasErrorCode("transfer_insufficient_balance"),  */}
      <DepotTransferInputMaskForm.Group name="amount" required={true}>
        {DepotTransferInputMaskForm.Validators.Integer()}
        {DepotTransferInputMaskForm.Validators.Range(
          1,
          maxAmount,
          undefined,
          maxAmount && (
            <>
              <Translate
                name="portfolio.transfer.amountDescription"
                as="div"
                args={[
                  <Number key={0} type="shares">
                    {maxAmount}
                  </Number>,
                ]}
              />
              {amount && (
                <Translate
                  name="portfolio.transfer.amountDescriptionDetailed"
                  as="div"
                  args={[
                    <Currency>{toMoney(toNumber(nominalValue) * parseInt(amount), nominalValue?.currency)}</Currency>,
                  ]}
                />
              )}
            </>
          ),
        )}
        <DepotTransferInputMaskForm.Input type="number" min={1} max={maxAmount?.toNumber()} step={1} />
      </DepotTransferInputMaskForm.Group>

      <DepotTransferInputMaskForm.Group name="isPlatformWallet">
        <DepotTransferInputMaskForm.GroupToggle>
          {['true', 'false'].map((option, index) => (
            <DepotTransferInputMaskForm.Radio value={option} key={index}>
              <Translate name={`commonOptions.${option}`} />
            </DepotTransferInputMaskForm.Radio>
          ))}
        </DepotTransferInputMaskForm.GroupToggle>
      </DepotTransferInputMaskForm.Group>

      {isPlatformWallet === 'true' ? (
        <Accordion
          label={<Translate name="portfolio.transfer.noAccountYet" as="span" />}
          expanded={invitationLinkExpanded}
          onChange={() => setInvitationLinkExpanded(!invitationLinkExpanded)}
        >
          <Styled.InputTransfer>
            <CopyInputGroup valueToCopy={makeLink(INVESTMENT_ROUTES.REGISTER, {}, true)} disabled />
          </Styled.InputTransfer>
          <small>
            <Translate name="portfolio.transfer.invitationLinkDescription" />
          </small>
        </Accordion>
      ) : (
        <>
          <Checkbox checked={isConfirmWalletSelected} onChange={onChangeConfirmWalletCheckbox}>
            <Translate name="portfolio.transfer.confirmOwningWallet" />
          </Checkbox>
          <small>
            <Translate name="portfolio.transfer.ownerWalletDescription" />
          </small>
        </>
      )}

      <DepotTransferInputMaskForm.GenericErrorMessages />
    </DepotTransferInputMaskForm.I18nKey>
  );
};

export default DepotTransferInputMask;
